import { Link } from 'aos-frontend-config/src'
import { translateEnum } from 'aos-helpers/src/helpers/translations/Translations'
import { canViewFodSelector, canViewTasksSelector } from 'aos-services/src/core/auth/state'
import { isRbacEnabled } from 'aos-services/src/services/auth/types/permissions'
import { taskManagerTabs } from 'aos-services/src/services/flightInformation/types/AosAirport'
import {
    Box,
    HStack,
    NavTab,
    Popover,
    PopoverBackdrop,
    PopoverContent,
    Pressable,
    Text,
} from 'aos-ui-universal'
import { useOverflowTabs } from 'aos-ui-universal/src/useOverflowTabs'
import { EnumValues } from 'enum-values'
import { TFunction } from 'i18next'
import React, { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { NavLink, useNavigate } from 'react-router-dom'

export enum FilterType {
    Timeline = 'Timeline',
    List = 'List',
    ShiftNotes = 'ShiftNotes',
    FireFighters = 'FireFighters',
    Reports = 'Reports',
    FOD = 'FOD',
    WeeklyBoard = 'WeeklyBoard',
    Templates = 'Templates',
}

interface TasksHeaderFilterProps {
    value: FilterType
}

const translateFilter = (t: TFunction) =>
    translateEnum<FilterType>(t)(FilterType, 'tasks.filter-type')

const SubgroupFilterType = taskManagerTabs.reduce(
    (acc, key) => {
        if (key in FilterType) {
            acc[key] = key
        }
        return acc
    },
    {} as Record<string, string>,
)

const linksList: Record<FilterType, string> = {
    [FilterType.List]: Link.TasksList,
    [FilterType.Timeline]: Link.TasksTimeline,
    [FilterType.Reports]: Link.TasksReports,
    [FilterType.ShiftNotes]: Link.TasksShiftNotes,
    [FilterType.FireFighters]: Link.TasksFireFighters,
    [FilterType.FOD]: Link.FOD,
    [FilterType.WeeklyBoard]: Link.TaskWeeklyBoard,
    [FilterType.Templates]: Link.TaskTemplates,
}

export const TasksHeaderFilter: FC<TasksHeaderFilterProps> = ({ value }) => {
    const { t } = useTranslation()
    const canViewTasks = useSelector(canViewTasksSelector)
    const canViewFod = useSelector(canViewFodSelector)
    const [isMoreOpen, setMoreOpen] = useState(false)
    const navigate = useNavigate()

    const headerItems = useMemo(
        () =>
            EnumValues.getValues<FilterType>(SubgroupFilterType).filter(i => {
                if (i === FilterType.Reports) {
                    return false
                }
                if (!isRbacEnabled()) {
                    return true
                }
                return i === FilterType.FOD ? canViewFod : canViewTasks
            }),
        [canViewTasks, canViewFod],
    )

    const { containerRef, measureRef, visible, overflow } = useOverflowTabs(headerItems, value)

    return (
        <Box ref={containerRef} className='min-w-0 flex-1 justify-center'>
            <HStack
                ref={measureRef}
                className='pointer-events-none absolute -z-10 flex-row opacity-0'
                aria-hidden
            >
                {headerItems.map(filterType => (
                    <Text key={filterType} size='heading' className='whitespace-nowrap px-3'>
                        {translateFilter(t)(filterType)}
                    </Text>
                ))}
            </HStack>

            <HStack className='items-center gap-6'>
                {visible.map(filterType => (
                    <NavLink key={filterType} to={linksList[filterType]} className='no-underline'>
                        <NavTab
                            label={translateFilter(t)(filterType)}
                            selected={filterType === value}
                        />
                    </NavLink>
                ))}
                {overflow.length > 0 && (
                    <Popover
                        isOpen={isMoreOpen}
                        onClose={() => setMoreOpen(false)}
                        trapFocus={false}
                        focusScope={false}
                        placement='bottom left'
                        trigger={triggerProps => (
                            <Pressable
                                {...triggerProps}
                                onPress={() => setMoreOpen(open => !open)}
                                accessibilityRole='button'
                                accessibilityState={{ expanded: isMoreOpen }}
                                className='shrink-0'
                            >
                                <NavTab
                                    label={t('more', { ns: 'flight-information' })}
                                    selected={false}
                                    badge={
                                        <Box className='items-center justify-center rounded bg-background-elevation px-1.5'>
                                            <Text size='caption' tone='muted'>
                                                {overflow.length}
                                            </Text>
                                        </Box>
                                    }
                                />
                            </Pressable>
                        )}
                    >
                        <PopoverBackdrop />
                        <PopoverContent className='min-w-44 max-w-none rounded border border-solid border-border-subtle bg-background-elevation py-1'>
                            <Box>
                                {overflow.map(filterType => (
                                    <Pressable
                                        key={filterType}
                                        accessibilityRole='menuitem'
                                        onPress={() => {
                                            setMoreOpen(false)
                                            navigate(linksList[filterType])
                                        }}
                                        className='px-4 py-2 hover:bg-background-surface-hover'
                                    >
                                        <Text
                                            size='body'
                                            tone={filterType === value ? 'default' : 'muted'}
                                            className='whitespace-nowrap'
                                        >
                                            {translateFilter(t)(filterType)}
                                        </Text>
                                    </Pressable>
                                ))}
                            </Box>
                        </PopoverContent>
                    </Popover>
                )}
            </HStack>
        </Box>
    )
}
